import React from 'react'
import styled from 'styled-components'
import type { FeaturedProduct } from '@src/types'
import { getProductImage, handleImageError } from '@src/utils/productImages'
import { colors } from '@src/theme/sharedComponents'
import { formatPrice } from '@src/utils/featuredProducts'

export const Card = styled.div<{ $withDivider?: boolean }>`
  display: flex;
  flex-direction: row;
  cursor: pointer;
  overflow: hidden;
  border-radius: 0;
  background: ${colors.primaryDark};
  position: relative;
  border-left: 3px solid ${colors.primary};
  ${p => p.$withDivider && 'border-top: 1px solid rgba(255,255,255,0.08);'}
  transition: border-left-color 0.15s, background 0.15s;

  &:hover {
    background: color-mix(in srgb, var(--color-primary) 20%, ${colors.primaryDark});
    border-left-color: ${colors.accent};
  }
`

export const ImgArea = styled.div`
  flex: 0 0 130px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0.5rem;
  background: rgba(255,255,255,0.06);
  position: relative;

  img {
    max-width: 100%;
    max-height: 90px;
    object-fit: contain;
    filter: drop-shadow(0 4px 12px rgba(0,0,0,0.5));
  }

  @media (max-width: 400px) {
    flex: 0 0 100px;
    img { max-height: 70px; }
  }
`

const ImgOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: white;
  background: rgba(0,0,0,0.5);
  opacity: 0;
  transition: opacity 0.15s;
  pointer-events: none;

  ${Card}:hover & { opacity: 1; }
`

export const TextArea = styled.div`
  flex: 1;
  padding: 0.7rem 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  overflow: hidden;
`

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
`

const FeaturedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.58rem;
  font-weight: 800;
  color: ${colors.primaryDark};
  background: ${colors.accent};
  padding: 0.15rem 0.5rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  flex-shrink: 0;
`

const MfrBadge = styled.div`
  display: inline-flex;
  align-items: center;
  font-size: 0.65rem;
  font-weight: 600;
  color: rgba(255,255,255,0.9);
  background: rgba(255,255,255,0.1);
  padding: 0.15rem 0.5rem;
  flex-shrink: 0;
`

const ProductName = styled.div`
  font-size: 0.92rem;
  font-weight: 700;
  color: white;
  line-height: 1.3;
  letter-spacing: 0.01em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const PromoText = styled.div`
  font-size: 0.72rem;
  color: ${colors.accent};
  font-weight: 600;
  letter-spacing: 0.03em;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  flex-wrap: wrap;
  margin-top: auto;
`

const Price = styled.span`
  font-size: 1.25rem;
  font-weight: 800;
  color: white;
  letter-spacing: -0.01em;
  line-height: 1;
`

const WasPrice = styled.span`
  font-size: 0.78rem;
  color: rgba(255,255,255,0.4);
  text-decoration: line-through;
`

const SaveBadge = styled.span`
  font-size: 0.6rem;
  font-weight: 800;
  background: ${colors.primary};
  color: white;
  padding: 0.1rem 0.45rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`

interface FeaturedProductCardProps {
  fp: FeaturedProduct
  onClick: () => void
  categoryImageMap: Record<string, string>
  withDivider?: boolean
  style?: React.CSSProperties
  footer?: React.ReactNode
}

const FeaturedProductCard: React.FC<FeaturedProductCardProps> = ({
  fp, onClick, categoryImageMap, withDivider = false, style, footer,
}) => {
  const product = fp.product
  if (!product) return null

  const salePrice = product.sale_price
  const savings = salePrice && product.price ? Math.round((1 - salePrice / product.price) * 100) : null
  const imgSrc = getProductImage(product.image_url, product.category, categoryImageMap)

  return (
    <Card $withDivider={withDivider} onClick={onClick} style={style}>
      <ImgArea>
        <img src={imgSrc} alt={product.short_description} onError={(e) => handleImageError(e, product.category, categoryImageMap)} />
        <ImgOverlay><i className="bi bi-eye" /> View</ImgOverlay>
      </ImgArea>
      <TextArea>
        <TopRow>
          <FeaturedBadge>
            <i className={salePrice ? 'bi bi-lightning-fill' : 'bi bi-star-fill'} />
            {salePrice ? 'Sale' : 'Featured'}
          </FeaturedBadge>
          {(fp.manufacturer_image_url || product.manufacturer) && (
            <MfrBadge>
              {fp.manufacturer_image_url
                ? <img src={fp.manufacturer_image_url} alt={product.manufacturer || ''} style={{ maxHeight: 12, maxWidth: 48, objectFit: 'contain' }} />
                : product.manufacturer}
            </MfrBadge>
          )}
        </TopRow>
        <ProductName>{product.product_type}</ProductName>
        {fp.promo_text && <PromoText>{fp.promo_text}</PromoText>}
        <PriceRow>
          <Price>{formatPrice(salePrice ?? product.price)}</Price>
          {salePrice && product.price && (
            <>
              <WasPrice>{formatPrice(product.price)}</WasPrice>
              {savings && <SaveBadge>-{savings}%</SaveBadge>}
            </>
          )}
        </PriceRow>
        {footer}
      </TextArea>
    </Card>
  )
}

export default FeaturedProductCard
